import React, { useState } from 'react';

export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const fmt = (n) => {
  if (!isFinite(n)) return '$0.00';
  const num = Number(n);
  const abs = Math.abs(num).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  return num < 0 ? `-$${abs}` : `$${abs}`;
};
export const fmtInt = (n) => (isFinite(n) ? '$' + Math.round(Number(n)).toLocaleString() : '$0');
export const pct = (n) => (isFinite(n) ? Number(n).toFixed(2) + '%' : '0%');

// Donut/Pie chart. `parts` = [{ label, value, color }]
export function PieChart({ parts, size = 180, showPercent = true }) {
  const [hover, setHover] = useState(null);
  const [tt, setTt] = useState(null); // { x, y, label, value, percent }
  const total = parts.reduce((a, b) => a + Math.max(0, b.value), 0) || 1;
  const r = size / 2 - 8;
  const cx = size / 2, cy = size / 2;
  let acc = 0;
  const arcs = parts.filter(p => p.value > 0).map((p, i) => {
    const a0 = (acc / total) * 2 * Math.PI - Math.PI / 2; acc += p.value;
    const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const [x0, y0] = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
    const [x1, y1] = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
    const mid = (a0 + a1) / 2;
    const [lx, ly] = [cx + (r * 0.65) * Math.cos(mid), cy + (r * 0.65) * Math.sin(mid)];
    return { i, d: `M${cx},${cy} L${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} Z`, color: p.color, label: p.label, value: p.value, lx, ly, percent: (p.value / total) * 100 };
  });
  return (
    <div className="flex items-center gap-4 relative">
      <svg viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0" style={{ width: size, height: size, overflow: 'visible' }}>
        {arcs.map((a) => (
          <path key={a.i} d={a.d} fill={a.color} stroke="#fff" strokeWidth="1.5"
            onMouseEnter={() => setHover(a.i)}
            onMouseMove={(e) => {
              const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
              setTt({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: a.label, value: a.value, percent: a.percent, color: a.color });
            }}
            onMouseLeave={() => { setHover(null); setTt(null); }}
            style={{
              cursor: 'pointer',
              transformOrigin: `${cx}px ${cy}px`,
              transform: hover === a.i ? 'scale(1.05)' : 'scale(1)',
              filter: hover === a.i ? 'brightness(1.1) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
              transition: 'transform 150ms ease, filter 150ms ease',
              opacity: hover !== null && hover !== a.i ? 0.55 : 1,
            }}
          />
        ))}
        {showPercent && arcs.map((a) => a.percent >= 4 && (
          <text key={'t'+a.i} x={a.lx} y={a.ly + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" pointerEvents="none">{Math.round(a.percent)}%</text>
        ))}
      </svg>
      {tt && (
        <div
          className="absolute pointer-events-none rounded-md px-3 py-2 text-xs bg-slate-900 text-white shadow-lg z-30"
          style={{ left: `${tt.x + 10}px`, top: `${tt.y - 10}px`, minWidth: 130, transform: 'translateY(-100%)' }}
          data-testid="pie-tooltip"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: tt.color }} />
            <span className="font-semibold">{tt.label}</span>
          </div>
          <div>{fmt(tt.value)} <span className="text-slate-300">({tt.percent.toFixed(1)}%)</span></div>
        </div>
      )}
      <div className="space-y-1.5 text-xs">
        {arcs.map((a) => (
          <div key={a.i}
            onMouseEnter={() => setHover(a.i)} onMouseLeave={() => setHover(null)}
            className={`flex items-center gap-2 cursor-pointer px-1 py-0.5 rounded ${hover === a.i ? 'bg-slate-100' : ''}`}>
            <span className="w-3 h-3 rounded" style={{ background: a.color }} />
            <span className="text-slate-700"><b>{a.label}</b> — {fmt(a.value)} <span className="text-slate-400">({Math.round(a.percent)}%)</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Line graph showing Balance / cumulative Interest / cumulative Payment over years.
// yearly = [{ year, interest, principal, balance, payment }]
export function BalanceGraph({ yearly, startBalance, showPayment = true }) {
  const [hover, setHover] = useState(null); // { x, i }
  if (!yearly || !yearly.length) return null;
  const w = 640, h = 260, padL = 60, padR = 20, padT = 15, padB = 38;
  const years = yearly.length;
  let cumI = 0, cumP = 0;
  const pts = yearly.map((y, i) => {
    cumI += y.interest; cumP += (y.payment || (y.interest + y.principal));
    return { x: i + 1, balance: y.balance, interest: cumI, payment: cumP };
  });
  const max = Math.max(startBalance, ...pts.map(p => p.payment)) || 1;
  const xs = (i) => padL + ((i) / years) * (w - padL - padR);
  const ys = (v) => {
    const y = h - padB - ((Number.isFinite(v) ? v : 0) / max) * (h - padT - padB);
    return Number.isFinite(y) ? y : h - padB;
  };
  const line = (arr, key) => arr.map((p) => `${xs(p.x)},${ys(p[key])}`).join(' ');
  const balPts = `${xs(0)},${ys(startBalance)} ` + line(pts, 'balance');
  const intPts = `${xs(0)},${ys(0)} ` + line(pts, 'interest');
  const payPts = `${xs(0)},${ys(0)} ` + line(pts, 'payment');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * max);
  const xTicks = Math.min(6, years);
  const hoverPt = hover !== null ? pts[hover] : null;
  return (
    <div className="overflow-auto relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[500px]"
        onMouseLeave={() => setHover(null)}
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const svgX = ((e.clientX - rect.left) / rect.width) * w;
          const idx = Math.round(((svgX - padL) / (w - padL - padR)) * years) - 1;
          setHover(idx >= 0 && idx < years ? idx : null);
        }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={ys(t)} x2={w - padR} y2={ys(t)} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={padL - 8} y={ys(t) + 4} textAnchor="end" fontSize="10" fill="#64748b">${Math.round(t/1000)}K</text>
          </g>
        ))}
        <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} stroke="#94a3b8" />
        {Array.from({ length: xTicks + 1 }, (_, i) => {
          const yr = Math.round(i * years / xTicks);
          return <text key={i} x={xs(yr)} y={h - padB + 16} textAnchor="middle" fontSize="10" fill="#64748b">{yr}</text>;
        })}
        <text x={w/2} y={h-4} textAnchor="middle" fontSize="11" fill="#334155">Year</text>
        <polyline points={balPts} fill="none" stroke="#2563eb" strokeWidth="2.5" />
        <polyline points={intPts} fill="none" stroke="#10b981" strokeWidth="2.5" />
        {showPayment && <polyline points={payPts} fill="none" stroke="#ef4444" strokeWidth="2.5" />}
        {hoverPt && (
          <g pointerEvents="none">
            <line x1={xs(hoverPt.x)} y1={padT} x2={xs(hoverPt.x)} y2={h - padB} stroke="#0f172a" strokeDasharray="2 3" strokeOpacity="0.5" />
            <circle cx={xs(hoverPt.x)} cy={ys(hoverPt.balance)} r="4" fill="#2563eb" stroke="#fff" strokeWidth="2" />
            <circle cx={xs(hoverPt.x)} cy={ys(hoverPt.interest)} r="4" fill="#10b981" stroke="#fff" strokeWidth="2" />
            {showPayment && <circle cx={xs(hoverPt.x)} cy={ys(hoverPt.payment)} r="4" fill="#ef4444" stroke="#fff" strokeWidth="2" />}
          </g>
        )}
      </svg>
      {hoverPt && (
        <div className="absolute pointer-events-none z-10 bg-slate-900 text-white text-[11px] px-2.5 py-1.5 rounded shadow-lg whitespace-nowrap"
          style={{ left: `calc(${(xs(hoverPt.x) / w) * 100}% + 12px)`, top: '10px' }}>
          <div className="font-bold mb-0.5">Year {hoverPt.x}</div>
          <div><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{background:'#2563eb'}}/>Balance: <b>{fmt(hoverPt.balance)}</b></div>
          <div><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{background:'#10b981'}}/>Interest: <b>{fmt(hoverPt.interest)}</b></div>
          {showPayment && <div><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{background:'#ef4444'}}/>Payment: <b>{fmt(hoverPt.payment)}</b></div>}
        </div>
      )}
      <div className="flex flex-wrap gap-4 justify-center mt-2 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#2563eb'}}/>Balance</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#10b981'}}/>Interest</span>
        {showPayment && <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#ef4444'}}/>Payment</span>}
      </div>
    </div>
  );
}

// Amortization table with Annual/Monthly tabs
// schedule = { yearly:[{year,interest,principal,balance}], monthly:[{month,interest,principal,balance,date?}] }
export function AmortTable({ schedule, defaultView = 'annual' }) {
  const [view, setView] = useState(defaultView);
  const rows = view === 'annual' ? schedule.yearly : schedule.monthly;
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <button onClick={() => setView('annual')} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${view==='annual'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`} data-testid="amort-annual">Annual Schedule</button>
        <button onClick={() => setView('monthly')} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${view==='monthly'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`} data-testid="amort-monthly">Monthly Schedule</button>
      </div>
      <div className="max-h-96 overflow-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-blue-800 text-white sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">{view==='annual'?'Year':'Month'}</th>
              <th className="px-3 py-2 text-right">Interest</th>
              <th className="px-3 py-2 text-right">Principal</th>
              <th className="px-3 py-2 text-right">Ending Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={`border-b border-slate-100 ${i%2?'bg-slate-50':''}`}>
                <td className="px-3 py-2">{view==='annual'?r.year:r.month}</td>
                <td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td>
                <td className="px-3 py-2 text-right text-blue-700">{fmt(r.principal)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Standard amortized loan schedule builder
export function buildAmortization(principal, annualRate, months, startMonth = 1, startYear = new Date().getFullYear()) {
  const r = annualRate / 100 / 12;
  const n = months;
  const M = r === 0 ? principal / n : (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  let bal = principal;
  const monthly = [];
  const yearly = [];
  let yInt = 0, yPrin = 0, yPay = 0;
  let curMonth = startMonth, curYear = startYear;
  for (let i = 1; i <= n; i++) {
    const interest = bal * r;
    const princ = M - interest;
    bal = Math.max(0, bal - princ);
    monthly.push({ month: `${curYear}-${String(curMonth).padStart(2,'0')}`, interest, principal: princ, balance: bal, payment: M });
    yInt += interest; yPrin += princ; yPay += M;
    if (i % 12 === 0 || i === n) {
      yearly.push({ year: Math.ceil(i / 12), interest: yInt, principal: yPrin, balance: bal, payment: yPay });
      yInt = 0; yPrin = 0; yPay = 0;
    }
    curMonth++; if (curMonth > 12) { curMonth = 1; curYear++; }
  }
  return { M, monthly, yearly, totalPaid: M * n, totalInterest: M * n - principal };
}

// Compound periods per year — full canonical list matching Calculator.net
export const COMPOUND_OPTIONS = [
  { key: 'continuously',  label: 'Continuously',       ppy: Infinity },
  { key: 'daily',         label: 'Daily',              ppy: 365 },
  { key: 'weekly',        label: 'Weekly',             ppy: 52 },
  { key: 'biweekly',      label: 'Biweekly',           ppy: 26 },
  { key: 'semimonthly',   label: 'Semi-monthly',       ppy: 24 },
  { key: 'monthly',       label: 'Monthly (APR)',      ppy: 12 },
  { key: 'quarterly',     label: 'Quarterly',          ppy: 4 },
  { key: 'semiannually',  label: 'Semi-annually',      ppy: 2 },
  { key: 'annually',      label: 'Annually (APY)',     ppy: 1 },
];
export const PAYBACK_OPTIONS = [
  { key: 'every-day',        label: 'Every Day',        ppy: 365 },
  { key: 'every-week',       label: 'Every Week',       ppy: 52 },
  { key: 'every-2-weeks',    label: 'Every 2 Weeks',    ppy: 26 },
  { key: 'every-half-month', label: 'Every Half Month', ppy: 24 },
  { key: 'every-month',      label: 'Every Month',      ppy: 12 },
  { key: 'every-quarter',    label: 'Every Quarter',    ppy: 4 },
  { key: 'every-6-months',   label: 'Every 6 Months',   ppy: 2 },
  { key: 'every-year',       label: 'Every Year',       ppy: 1 },
];
export function CompoundSelect({ value, onChange, id, className = '', testId }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} id={id} data-testid={testId} className={className}>
      {COMPOUND_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}
export function PaybackSelect({ value, onChange, id, className = '', testId }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} id={id} data-testid={testId} className={className}>
      {PAYBACK_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}
// Effective annual rate given nominal + compound key
export function effectiveAnnual(nominalPct, compoundKey) {
  const opt = COMPOUND_OPTIONS.find(o => o.key === compoundKey) || COMPOUND_OPTIONS[5];
  const r = nominalPct / 100;
  if (opt.ppy === Infinity) return Math.exp(r) - 1;
  return Math.pow(1 + r / opt.ppy, opt.ppy) - 1;
}
// Effective per-period rate for a given payment frequency, from nominal APR + its own compounding
export function periodicRate(nominalPct, compoundKey, paymentPpy) {
  const eff = effectiveAnnual(nominalPct, compoundKey);
  return Math.pow(1 + eff, 1 / paymentPpy) - 1;
}

// Bar chart. `data` = [{ label, values:[{value,color}] }], stackType 'stack'|'group'
export function BarChart({ data, stack = true, height = 260, yLabel = '$', legend = [] }) {
  const [hover, setHover] = useState(null); // {i, x, y, values, label}
  if (!data || !data.length) return null;
  const w = 640, padL = 60, padR = 20, padT = 20, padB = 40;
  const barW = Math.max(6, (w - padL - padR) / data.length * 0.7);
  const maxV = Math.max(1, ...data.map(d => stack ? d.values.reduce((a,b)=>a+Math.max(0,b.value),0) : Math.max(...d.values.map(v=>v.value))));
  const ys = v => height - padB - (v / maxV) * (height - padT - padB);
  const xs = i => padL + ((i + 0.5) / data.length) * (w - padL - padR);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * maxV);
  return (
    <div className="overflow-auto relative">
      <svg viewBox={`0 0 ${w} ${height}`} className="w-full min-w-[500px]">
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={ys(t)} x2={w - padR} y2={ys(t)} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={padL - 6} y={ys(t) + 4} textAnchor="end" fontSize="10" fill="#64748b">
              {yLabel}{t >= 1000 ? Math.round(t/1000)+'K' : Math.round(t)}
            </text>
          </g>
        ))}
        <line x1={padL} y1={height - padB} x2={w - padR} y2={height - padB} stroke="#94a3b8" />
        {data.map((d, i) => {
          if (stack) {
            let acc = 0;
            return d.values.map((v, j) => {
              const y0 = ys(acc); acc += Math.max(0, v.value); const y1 = ys(acc);
              return (
                <rect key={i+'-'+j}
                  x={xs(i) - barW/2} y={y1} width={barW} height={y0 - y1} fill={v.color}
                  onMouseEnter={() => setHover({ i, x: xs(i), y: y1, values: d.values, label: d.label })}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer', opacity: hover && hover.i !== i ? 0.6 : 1, transition: 'opacity 120ms ease' }} />
              );
            });
          } else {
            const bw = barW / d.values.length;
            return d.values.map((v, j) => (
              <rect key={i+'-'+j}
                x={xs(i) - barW/2 + j*bw} y={ys(v.value)} width={bw - 1} height={height - padB - ys(v.value)} fill={v.color}
                onMouseEnter={() => setHover({ i, x: xs(i), y: ys(v.value), values: d.values, label: d.label })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer', opacity: hover && hover.i !== i ? 0.6 : 1, transition: 'opacity 120ms ease' }} />
            ));
          }
        })}
        {data.map((d, i) => (
          <text key={'x-'+i} x={xs(i)} y={height - padB + 14} textAnchor="middle" fontSize="10" fill="#64748b">{d.label}</text>
        ))}
      </svg>
      {hover && (
        <div className="absolute pointer-events-none z-10 bg-slate-900 text-white text-[11px] px-2.5 py-1.5 rounded shadow-lg"
          style={{ left: `calc(${(hover.x / w) * 100}% + 8px)`, top: `${(hover.y / height) * 100}%`, transform: 'translateY(-100%)' }}>
          <div className="font-bold mb-0.5">{legend[0]?.tooltipLabel || 'Year'} {hover.label}</div>
          {hover.values.map((v, j) => (
            <div key={j} className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="w-2 h-2 rounded-sm" style={{ background: v.color }} />
              <span>{legend[j]?.label || v.label || `Series ${j+1}`}: <b>{fmt(v.value)}</b></span>
            </div>
          ))}
          {stack && <div className="border-t border-white/20 mt-1 pt-1">Total: <b>{fmt(hover.values.reduce((a,b)=>a+b.value,0))}</b></div>}
        </div>
      )}
      {legend.length > 0 && (
        <div className="flex flex-wrap gap-4 justify-center mt-2 text-xs">
          {legend.map((l, i) => (
            <span key={i} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:l.color}}/>{l.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// Full ISO 4217 currency name map (150+)
export const CURRENCY_FULL_NAMES = {
  USD: 'United States Dollar', EUR: 'Euro', GBP: 'British Pound Sterling', JPY: 'Japanese Yen',
  CNY: 'Chinese Yuan Renminbi', CAD: 'Canadian Dollar', AUD: 'Australian Dollar', INR: 'Indian Rupee',
  CHF: 'Swiss Franc', HKD: 'Hong Kong Dollar', SGD: 'Singapore Dollar', NZD: 'New Zealand Dollar',
  KRW: 'South Korean Won', MXN: 'Mexican Peso', BRL: 'Brazilian Real', RUB: 'Russian Ruble',
  ZAR: 'South African Rand', SEK: 'Swedish Krona', NOK: 'Norwegian Krone', TRY: 'Turkish Lira',
  AED: 'United Arab Emirates Dirham', AFN: 'Afghan Afghani', ALL: 'Albanian Lek', AMD: 'Armenian Dram',
  ANG: 'Netherlands Antillean Guilder', AOA: 'Angolan Kwanza', ARS: 'Argentine Peso', AWG: 'Aruban Florin',
  AZN: 'Azerbaijani Manat', BAM: 'Bosnia and Herzegovina Convertible Mark', BBD: 'Barbadian Dollar',
  BDT: 'Bangladeshi Taka', BGN: 'Bulgarian Lev', BHD: 'Bahraini Dinar', BIF: 'Burundian Franc',
  BMD: 'Bermudian Dollar', BND: 'Brunei Dollar', BOB: 'Bolivian Boliviano', BSD: 'Bahamian Dollar',
  BTN: 'Bhutanese Ngultrum', BWP: 'Botswana Pula', BYN: 'Belarusian Ruble', BZD: 'Belize Dollar',
  CDF: 'Congolese Franc', CLP: 'Chilean Peso', COP: 'Colombian Peso', CRC: 'Costa Rican Colón',
  CUP: 'Cuban Peso', CVE: 'Cape Verdean Escudo', CZK: 'Czech Koruna', DJF: 'Djiboutian Franc',
  DKK: 'Danish Krone', DOP: 'Dominican Peso', DZD: 'Algerian Dinar', EGP: 'Egyptian Pound',
  ERN: 'Eritrean Nakfa', ETB: 'Ethiopian Birr', FJD: 'Fijian Dollar', FKP: 'Falkland Islands Pound',
  GEL: 'Georgian Lari', GHS: 'Ghanaian Cedi', GIP: 'Gibraltar Pound', GMD: 'Gambian Dalasi',
  GNF: 'Guinean Franc', GTQ: 'Guatemalan Quetzal', GYD: 'Guyanese Dollar', HNL: 'Honduran Lempira',
  HRK: 'Croatian Kuna', HTG: 'Haitian Gourde', HUF: 'Hungarian Forint', IDR: 'Indonesian Rupiah',
  ILS: 'Israeli New Shekel', IQD: 'Iraqi Dinar', IRR: 'Iranian Rial', ISK: 'Icelandic Króna',
  JMD: 'Jamaican Dollar', JOD: 'Jordanian Dinar', KES: 'Kenyan Shilling', KGS: 'Kyrgyzstani Som',
  KHR: 'Cambodian Riel', KMF: 'Comorian Franc', KPW: 'North Korean Won', KWD: 'Kuwaiti Dinar',
  KYD: 'Cayman Islands Dollar', KZT: 'Kazakhstani Tenge', LAK: 'Laotian Kip', LBP: 'Lebanese Pound',
  LKR: 'Sri Lankan Rupee', LRD: 'Liberian Dollar', LSL: 'Lesotho Loti', LYD: 'Libyan Dinar',
  MAD: 'Moroccan Dirham', MDL: 'Moldovan Leu', MGA: 'Malagasy Ariary', MKD: 'Macedonian Denar',
  MMK: 'Burmese Kyat', MNT: 'Mongolian Tögrög', MOP: 'Macanese Pataca', MRU: 'Mauritanian Ouguiya',
  MUR: 'Mauritian Rupee', MVR: 'Maldivian Rufiyaa', MWK: 'Malawian Kwacha', MYR: 'Malaysian Ringgit',
  MZN: 'Mozambican Metical', NAD: 'Namibian Dollar', NGN: 'Nigerian Naira', NIO: 'Nicaraguan Córdoba',
  NPR: 'Nepalese Rupee', OMR: 'Omani Rial', PAB: 'Panamanian Balboa', PEN: 'Peruvian Sol',
  PGK: 'Papua New Guinean Kina', PHP: 'Philippine Peso', PKR: 'Pakistani Rupee', PLN: 'Polish Zloty',
  PYG: 'Paraguayan Guarani', QAR: 'Qatari Riyal', RON: 'Romanian Leu', RSD: 'Serbian Dinar',
  RWF: 'Rwandan Franc', SAR: 'Saudi Riyal', SBD: 'Solomon Islands Dollar', SCR: 'Seychellois Rupee',
  SDG: 'Sudanese Pound', SHP: 'Saint Helena Pound', SLL: 'Sierra Leonean Leone', SOS: 'Somali Shilling',
  SRD: 'Surinamese Dollar', SSP: 'South Sudanese Pound', STN: 'São Tomé and Príncipe Dobra',
  SVC: 'Salvadoran Colón', SYP: 'Syrian Pound', SZL: 'Swazi Lilangeni', THB: 'Thai Baht',
  TJS: 'Tajikistani Somoni', TMT: 'Turkmenistani Manat', TND: 'Tunisian Dinar', TOP: 'Tongan Paʻanga',
  TTD: 'Trinidad and Tobago Dollar', TWD: 'New Taiwan Dollar', TZS: 'Tanzanian Shilling',
  UAH: 'Ukrainian Hryvnia', UGX: 'Ugandan Shilling', UYU: 'Uruguayan Peso', UZS: 'Uzbekistani Som',
  VES: 'Venezuelan Bolívar Soberano', VND: 'Vietnamese Dong', VUV: 'Vanuatu Vatu', WST: 'Samoan Tala',
  XAF: 'Central African CFA Franc', XCD: 'East Caribbean Dollar', XOF: 'West African CFA Franc',
  XPF: 'CFP Franc', YER: 'Yemeni Rial', ZMW: 'Zambian Kwacha', ZWL: 'Zimbabwean Dollar'
};
